'use client';

import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { useSession } from '../context/SessionContext';
import ShellHeader from './chrome/ShellHeader';
import Breadcrumb, { type Crumb } from './chrome/Breadcrumb';
import VesselSelector from './chrome/VesselSelector';
import AppSwitcher from './chrome/AppSwitcher';
import { useAgentHubApps, findAppByPath } from '@/hooks/useAgentHubApps';
import { HEADER_STATUS } from './chrome/headerStatus';

// Split out of the initial bundle the way next/dynamic used to do it. The sidebar is
// behind the hamburger and is not needed to paint the first screen.
const EnhancedOverlaySidebar = lazy(() => import('./EnhancedOverlaySidebar'));

interface SimpleLayoutProps {
  children: React.ReactNode;
  title?: string;
  currentPath?: string;
  /**
   * Overrides the trail this layout would derive from `currentPath`. The Agent Hub
   * passes its own, because it is not one of the menu's apps and has no record to
   * derive a category from.
   */
  crumbs?: Crumb[];
  /** Same, for the italic line under the trail. Defaults to the app's description. */
  subtitle?: string;
  /** Header title (e.g. Agent Hub / Ship Management). Defaults by path. */
  headerTitle?: string;
}

const HUB_PATH = '/agent-hub';
const HUB_CRUMB_LABEL = 'Agent Hub';

const SimpleLayout = React.memo(function SimpleLayout({
  children,
  title,
  currentPath = '/',
  crumbs,
  subtitle,
  headerTitle,
}: SimpleLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const router = useShellRouter();

  const { tenant, user } = useSession();

  const isExternalNode = tenant === 'cognixone' || tenant === 'sandbox';

  const [onlyMicroapps, setOnlyMicroapps] = useState<boolean | null>(null);

  const showBackToSiya =
    Boolean(tenant) && !isExternalNode && !isEmbedded && onlyMicroapps === false;

  const { apps } = useAgentHubApps(tenant, user?.id);
  const currentApp = useMemo(() => findAppByPath(apps, currentPath), [apps, currentPath]);

  const siblings = useMemo(() => {
    if (!currentApp?.category) return [];
    return apps.filter((app) => app.category === currentApp.category);
  }, [apps, currentApp]);

  const resolvedCrumbs = useMemo<Crumb[]>(() => {
    if (crumbs) return crumbs;

    const hub: Crumb = { label: HUB_CRUMB_LABEL, to: HUB_PATH };

    // Agent Hub pages served by a micro-app (Ship Management, from agenthub-overview) have
    // no menu record: they sit straight under Agent Hub.
    if (!currentApp && currentPath.startsWith(`${HUB_PATH}/`)) {
      return title ? [hub, { label: title }] : [hub];
    }

    const leafLabel = currentApp
      ? currentApp.category
        ? `${currentApp.category}: ${currentApp.label}`
        : currentApp.label
      : title;

    // App pages sit under Ship Management in the trail.
    const ship: Crumb = { label: 'Ship Management', to: '/agent-hub/ship-management' };
    return leafLabel ? [hub, ship, { label: leafLabel }] : [hub, ship];
  }, [crumbs, currentApp, currentPath, title]);

  const resolvedSubtitle = subtitle ?? currentApp?.description;

  const initials = useMemo(() => {
    const first = user?.firstName?.trim()?.[0] ?? '';
    const last = user?.lastName?.trim()?.[0] ?? '';
    if (first || last) return `${first}${last}`.toUpperCase();
    const source = user?.fullName ?? user?.email ?? '';
    return source.trim().slice(0, 2).toUpperCase();
  }, [user]);

  useEffect(() => {
    setIsPreviewMode(router.query.preview === 'true');
  }, [router.query.preview]);

  useEffect(() => {
    try {
      setIsEmbedded(window.self !== window.top);
    } catch {
      setIsEmbedded(true);
    }
  }, []);

  if (isPreviewMode) {
    return (
      <div className="shell-preview">
        <main className="shell-preview__main">{children}</main>
      </div>
    );
  }

  const resolvedHeaderTitle =
    headerTitle ??
    (currentPath.startsWith('/agent-hub/ship-management')
      ? 'Ship Management'
      : currentPath === '/agent-hub' || currentPath.startsWith('/agent-hub/')
        ? 'Agent Hub'
        : isExternalNode
          ? 'Apps'
          : 'Agent Hub');

  const showVesselSelector =
    Boolean(currentApp) ||
    currentPath.startsWith('/agent-hub/ship-management');

  return (
    <div className="shell-frame">
      <Suspense fallback={null}>
        <EnhancedOverlaySidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOnlyMicroappsResolved={setOnlyMicroapps}
        />
      </Suspense>

      <ShellHeader
        title={resolvedHeaderTitle}
        onOpenMenu={() => setSidebarOpen(true)}
        showLogo={!isExternalNode}
        lastUpdated={HEADER_STATUS.lastUpdated}
        notificationCount={HEADER_STATUS.notificationCount}
        initials={initials}
        userName={user?.fullName ?? [user?.firstName, user?.lastName].filter(Boolean).join(' ')}
      />

      <main className="shell-main">
        <Breadcrumb
          crumbs={resolvedCrumbs}
          subtitle={resolvedSubtitle}
          homeHref={showBackToSiya ? `https://${tenant}.siya.com` : undefined}
          actions={
            <>
              {showVesselSelector && <VesselSelector tenant={tenant} />}
              {currentApp && <AppSwitcher current={currentApp} siblings={siblings} />}
            </>
          }
        />

        {children}
      </main>

    </div>
  );
});

export default SimpleLayout;
